'use strict';

const { BucksBoxClient } = require('../BucksBoxClient');
const { h, clear } = require('./dom');
const { injectStyles, BX } = require('./theme');
const { AepsWidget } = require('./AepsWidget');
const { AepsOnboardingWidget } = require('./AepsOnboardingWidget');
const { DmtWidget } = require('./DmtWidget');
const { DmtOnboardingWidget } = require('./DmtOnboardingWidget');
const { UpiWidget } = require('./UpiWidget');
const { MerchantSignupWidget } = require('./MerchantSignupWidget');
const { MerchantOnboardingWidget } = require('./MerchantOnboardingWidget');

const WIDGETS = {
  upi: UpiWidget,
  aeps: AepsWidget,
  'aeps-onboarding': AepsOnboardingWidget,
  dmt: DmtWidget,
  'dmt-onboarding': DmtOnboardingWidget,
  'merchant-signup': MerchantSignupWidget,
  'merchant-onboarding': MerchantOnboardingWidget,
};

function base64UrlDecode(str) {
  str = str.replace(/-/g, '+').replace(/_/g, '/');
  while (str.length % 4) str += '=';
  return atob(str);
}

/**
 * Decodes options.token into a plain config object. This is a convenience
 * carrier, not a secure session credential — there is no server-side
 * mint/verify step (see the auth-model discussion this shipped with), so
 * it's exactly as trusted as passing clientId/aesKey/checksumSecret
 * directly. Two accepted shapes:
 *   - base64(JSON.stringify({...})) — the documented format.
 *   - a JWT-shaped "header.payload.signature" string — the payload segment
 *     is decoded as JSON and used as-is; the signature is never checked.
 */
function decodeToken(token) {
  try {
    return JSON.parse(atob(token));
  } catch (e) { /* fall through to JWT-shaped parsing */ }

  const parts = token.split('.');
  if (parts.length === 3) {
    try {
      return JSON.parse(base64UrlDecode(parts[1]));
    } catch (e) { /* fall through to the error below */ }
  }

  throw new Error('bucksbox: options.token could not be decoded — expected base64(JSON) or a JWT-shaped string');
}

// ── Global event bus for bucksboxHandler() ──────────────────────────
// Razorpay-style: one global listener registry, every open Checkout
// instance's result is broadcast to it (not scoped per-instance), matching
// the `bucksboxHandler(function (e) { if (e.data.status === 'success') ... })`
// integration shape.
const listeners = [];

function bucksboxHandler(callback) {
  listeners.push(callback);
  return () => { const i = listeners.indexOf(callback); if (i > -1) listeners.splice(i, 1); };
}

function emit(status, data) {
  const event = { data: { status, ...data } };
  listeners.forEach((cb) => { try { cb(event); } catch (e) { /* one bad listener shouldn't break the rest */ } });
}

/**
 * Checkout — Razorpay/Stripe-Checkout-style drop-in wrapper around
 * BucksBoxClient + the AEPS/AEPS-onboarding/DMT/DMT-onboarding/UPI/merchant-signup/merchant-onboarding
 * widgets: `new bucksbox(options)`,
 * `.open()` mounts a full-screen modal and starts the right widget,
 * `.close()` tears it down, and the global `bucksboxHandler(cb)` receives
 * every instance's outcome as `{ data: { status: 'success'|'failure', ... } }`.
 *
 * @example
 * var options = {
 *   environment: 'production', // or 'staging' — resolves to the right gateway URL internally.
 *                                // Pass gatewayUrl instead for local dev or any other URL.
 *   clientId: '...', key: '...',   // checksumSecret defaults to key — no need to pass it separately
 *   service: 'upi',      // 'upi' | 'aeps' | 'aeps-onboarding' | 'dmt' | 'dmt-onboarding' | 'merchant-signup' | 'merchant-onboarding' — default 'upi'
 *   amount: 499,          // optional — dynamic QR when set, static when omitted (UPI only)
 *   order_id: 'ORD123',   // optional
 * };
 * // For 'merchant-signup', clientId/key are the onboarding vendor's own credentials — paymentSystem
 * // links the new merchant to that vendor automatically (see MerchantSignupWidget's class doc).
 * // For 'merchant-onboarding', clientId/key are the merchant's own credentials (the same ones
 * // 'merchant-signup' just produced) — see MerchantOnboardingWidget's class doc.
 * // or, packed as a single opaque string instead of separate fields:
 * // var options = { token: btoa(JSON.stringify({ environment: 'production', clientId, key, amount, order_id })) };
 *
 * var buckspay = new bucksbox(options);
 * buckspay.open();
 * bucksboxHandler(function (e) {
 *   if (e.data.status === 'success') { buckspay.close(); console.log(e.data); }
 *   if (e.data.status === 'failure') { buckspay.close(); console.log(e.data); }
 * });
 *
 * Note: clientId and key are different paymentSystem fields — clientId
 * identifies who's calling, key is the actual AES-256-GCM key that encrypts
 * every call. `apiKey`/`aesKey` are still accepted as deprecated aliases
 * for `key`.
 */
class Checkout {
  constructor(options = {}) {
    const cfg = typeof options.token === 'string' ? decodeToken(options.token) : options;
    const key = cfg.key || cfg.apiKey || cfg.aesKey;

    if ((!cfg.gatewayUrl && !cfg.environment) || !cfg.clientId || !key) {
      throw new Error('bucksbox: (gatewayUrl or environment), clientId and key are required (directly, or decoded from options.token)');
    }

    this._client = new BucksBoxClient({
      gatewayUrl: cfg.gatewayUrl,
      environment: cfg.environment,
      clientId: cfg.clientId,
      key,
      checksumSecret: cfg.checksumSecret, // optional — BucksBoxClient/GatewayClient default this to key
      merchantId: cfg.merchantId,
    });

    this._service = cfg.service || 'upi';
    this._startParams = { amount: cfg.amount, order_id: cfg.order_id ?? cfg.orderId };
    this._overlay = null;
    this._widget = null;
  }

  open() {
    if (this._overlay) return this; // already open

    injectStyles();
    const WidgetClass = WIDGETS[this._service];
    if (!WidgetClass) throw new Error(`bucksbox: unknown service "${this._service}" — expected "upi", "aeps", "aeps-onboarding", "dmt", "dmt-onboarding", "merchant-signup" or "merchant-onboarding"`);

    this._resolved = false; // set true once onSuccess/onError has fired for this open()

    // The close button deliberately pokes outside the modal's box (negative
    // top/right offsets) — it anchors to .bbx-checkout-frame, a wrapper with
    // no overflow clipping of its own, not to .bbx-checkout-modal itself,
    // which clips (overflow-x/y) for its own scrollable content and would
    // otherwise hide the button.
    //
    // Deliberately no backdrop-click-to-close: a stray click outside the
    // modal (easy to trigger by accident, especially mid-form) must not
    // silently discard an in-progress onboarding/payment flow. Only the
    // explicit close button (✕) or the widget's own Done button closes it.
    this._overlay = h('div', { class: 'bbx-checkout-overlay' }, [
      h('div', { class: 'bbx-checkout-frame' }, [
        h('button', { class: 'bbx-checkout-close', text: '✕', onClick: () => this.close(true) }),
        h('div', { class: 'bbx-checkout-modal' }, [
          h('div', { class: 'bbx-checkout-body' }),
        ]),
      ]),
    ]);
    document.body.appendChild(this._overlay);

    const body = this._overlay.querySelector('.bbx-checkout-body');
    this._widget = new WidgetClass(this._client, {
      onSuccess: (result) => { this._resolved = true; emit('success', result); },
      onError: (err) => { this._resolved = true; emit('failure', err && err.response ? err.response : err); },
      onClose: () => this.close(),
    });
    this._widget.mount(body).start(this._startParams);

    return this;
  }

  /**
   * @param {boolean} [cancelled] - true when the caller is dismissing the
   *   modal directly (the ✕ close button — there is no backdrop-click-to-
   *   close, see open()), as opposed to the widget's own Done button
   *   (which already reported success/failure
   *   itself before calling onClose → close()). If nothing was ever
   *   reported for this open() — the customer bailed out mid-flow — that's
   *   still not silently unreported: the checkout page always gets exactly
   *   one success or failure per open(), so a bare dismissal is reported as
   *   a cancelled failure.
   */
  close(cancelled) {
    if (cancelled && !this._resolved) {
      this._resolved = true;
      emit('failure', { statusCode: '01', message: 'Payment cancelled by user' });
    }
    if (this._widget) { this._widget.unmount(); this._widget = null; }
    if (this._overlay && this._overlay.parentNode) this._overlay.parentNode.removeChild(this._overlay);
    this._overlay = null;
  }
}

if (typeof document !== 'undefined' && !document.getElementById('bbx-checkout-styles')) {
  const style = document.createElement('style');
  style.id = 'bbx-checkout-styles';
  style.textContent = `
    .bbx-checkout-overlay, .bbx-checkout-overlay * { box-sizing: border-box; }
    .bbx-checkout-overlay {
      position: fixed; inset: 0; background: rgba(15,23,42,.6); z-index: 999998;
      display: flex; align-items: center; justify-content: center; padding: 16px;
      overflow-x: hidden;
    }
    .bbx-checkout-frame { position: relative; width: 100%; max-width: 460px; }
    .bbx-checkout-modal {
      width: 100%; max-height: 92vh;
      overflow-y: auto; overflow-x: hidden;
    }
    .bbx-checkout-close {
      position: absolute; top: -14px; right: -14px; width: 32px; height: 32px; border-radius: 50%;
      border: none; background: #fff; color: ${BX.ink}; font-size: 15px; cursor: pointer;
      box-shadow: 0 4px 14px rgba(15,23,42,.25); z-index: 2;
    }
    @media (max-width: 400px) {
      .bbx-checkout-overlay { padding: 10px; }
      .bbx-checkout-close { top: -10px; right: -10px; width: 28px; height: 28px; font-size: 13px; }
    }
  `;
  document.head.appendChild(style);
}

module.exports = { Checkout, bucksboxHandler };
